import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Mail, Eye, MousePointer, UserMinus, TrendingUp, XCircle } from 'lucide-react';

interface CalculatorCardProps {
  title: string;
  description: string;
  link: string;
  icon: React.ElementType;
}

const EmailMarketingPerformancePage = () => {
  const siteUrl = 'https://www.marketingcalculatorhub.com';
  const pageUrl = `${siteUrl}/email-marketing-performance`;
  const logoUrl = `${siteUrl}/logo.png`;

  return (
    <div className="container mx-auto px-4 py-8">
      <Helmet>
        <title>Email Marketing Performance Calculators: Boost Your Campaigns | Free Tools</title>
        <meta name="description" content="Optimize your email marketing with our free performance calculators. Measure open rates, CTR, ROI, and more. Improve your email campaigns now!" />
        <meta name="keywords" content="email marketing calculators, open rate, click-through rate, ROI, unsubscribe rate, free marketing tools" />
        <link rel="canonical" href={pageUrl} />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:title" content="Free Email Marketing Performance Calculators: Boost Your Campaigns" />
        <meta property="og:description" content="Enhance your email marketing with our free calculators. Measure open rates, CTR, ROI, and more. Improve your email campaigns now!" />
        <meta property="og:image" content={logoUrl} />

        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content={pageUrl} />
        <meta property="twitter:title" content="Free Email Marketing Performance Calculators: Boost Your Campaigns" />
        <meta property="twitter:description" content="Enhance your email marketing with our free calculators. Measure open rates, CTR, ROI, and more. Improve your email campaigns now!" />
        <meta property="twitter:image" content={logoUrl} />

        {/* Schema.org JSON-LD */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": "Email Marketing Performance Calculators",
            "description": "Optimize your email marketing performance with our suite of calculators. Measure open rates, CTR, ROI, and more.",
            "url": pageUrl,
            "image": logoUrl,
            "mainEntityOfPage": {
              "@type": "WebPage",
              "@id": pageUrl
            },
            "breadcrumb": {
              "@type": "BreadcrumbList",
              "itemListElement": [{
                "@type": "ListItem",
                "position": 1,
                "name": "Home",
                "item": siteUrl
              }, {
                "@type": "ListItem",
                "position": 2,
                "name": "Email Marketing Performance Calculators",
                "item": pageUrl
              }]
            }
          })}
        </script>
      </Helmet>
      <h1 className="text-3xl font-bold text-gray-800 mb-6">Email Marketing Performance</h1>
      
      <section className="mb-8">
        <h2 className="text-2xl font-semibold text-gray-700 mb-4">Understanding Email Marketing Performance</h2>
        <p className="text-gray-600 mb-4">
          Email marketing performance metrics are crucial for evaluating the effectiveness of your email campaigns. These metrics help you understand how well your emails are resonating with your audience, identify areas for improvement, and optimize your email marketing strategy.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold text-gray-700 mb-4">Email Marketing Performance Calculators</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <CalculatorCard 
            title="Email Open Rate" 
            description="Calculate the percentage of recipients who open your emails" 
            link="/calculators/email-open-rate" 
            icon={Eye}
          />
          <CalculatorCard 
            title="Email Click-Through Rate" 
            description="Measure the click-through rate of your email campaigns" 
            link="/calculators/email-click-through-rate" 
            icon={MousePointer}
          />
          <CalculatorCard 
            title="Email Unsubscribe Rate" 
            description="Calculate the rate at which recipients unsubscribe from your emails" 
            link="/calculators/email-unsubscribe-rate" 
            icon={UserMinus}
          />
          <CalculatorCard 
            title="Email Campaign ROI" 
            description="Measure the return on investment for your email campaigns" 
            link="/calculators/email-campaign-roi" 
            icon={TrendingUp}
          />
          <CalculatorCard 
            title="Newsletter Churn Rate" 
            description="Calculate the rate at which subscribers leave your newsletter" 
            link="/calculators/newsletter-churn-rate" 
            icon={XCircle}
          />
        </div>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold text-gray-700 mb-4">FAQ</h2>
        <div className="space-y-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-700">Why are email marketing performance metrics important?</h3>
            <p className="text-gray-600">These metrics help you understand how your audience engages with your emails, allowing you to optimize your campaigns for better results and higher ROI.</p>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-700">How often should I analyze these metrics?</h3>
            <p className="text-gray-600">It's recommended to review your email marketing performance metrics regularly, ideally after each campaign and on a monthly basis to identify trends and areas for improvement.</p>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-700">How can I improve my email marketing performance?</h3>
            <p className="text-gray-600">Focus on creating engaging subject lines, personalized content, mobile-friendly designs, and segmenting your audience. Regularly test different elements of your emails and use the insights from these metrics to refine your strategy.</p>
          </div>
        </div>
      </section>
    </div>
  );
};

const CalculatorCard: React.FC<CalculatorCardProps> = ({ title, description, link, icon: Icon }) => (
  <Link to={link} className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
    <Icon className="w-8 h-8 text-indigo-600 mb-4" />
    <h3 className="text-lg font-semibold text-gray-800 mb-2">{title}</h3>
    <p className="text-gray-600 mb-4">{description}</p>
    <span className="text-indigo-600 font-medium">Use Calculator</span>
  </Link>
);

export default EmailMarketingPerformancePage;
